import { useEffect, useState } from "react";
import { Smartphone, X, ChevronRight } from "lucide-react";

const DISMISSED_KEY = "epro_install_dismissed_at";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
}

function recentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISSED_KEY);
    if (!v) return false;
    return Date.now() - Number(v) < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

export default function InstallPwaBanner() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari: show a custom help banner since browser-prompt isn't available
    if (isIos()) {
      setShow(true);
    }

    const onInstalled = () => {
      setShow(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch {}
    setShow(false);
    setShowIosHelp(false);
  };

  const install = async () => {
    if (isIos()) {
      setShowIosHelp(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setShow(false);
    setDeferred(null);
    if (outcome === "dismissed") dismiss();
  };

  if (!show) return null;

  return (
    <>
      <div
        data-testid="install-pwa-banner"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60] bg-[#1A1918] text-[#FAFAFA] border border-white/10 shadow-2xl"
      >
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="bg-[#C86641] text-[#FAFAFA] w-11 h-11 shrink-0 flex items-center justify-center">
              <Smartphone size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg tracking-tight">Installez E-PRO sur votre téléphone</div>
              <p className="text-[13px] text-[#A8A7A3] mt-1 leading-relaxed">
                Accédez à vos devis et discutez en direct depuis votre écran d'accueil.
              </p>
            </div>
            <button
              onClick={dismiss}
              data-testid="install-pwa-dismiss"
              className="text-[#A8A7A3] hover:text-[#FAFAFA] -mt-1 -mr-1"
              aria-label="Fermer"
            >
              <X size={18} />
            </button>
          </div>
          <button
            onClick={install}
            data-testid="install-pwa-cta"
            className="mt-4 w-full bg-[#C86641] hover:bg-[#A65132] transition-colors px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"
          >
            Installer l'application <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {showIosHelp && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={dismiss}>
          <div
            data-testid="install-pwa-ios-help"
            className="bg-[#FAFAFA] text-[#1A1918] max-w-md w-full border border-[#E5E3DD] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#C86641]">◆ Installation iPhone</div>
              <button onClick={dismiss} className="text-[#5C5A56]"><X size={18} /></button>
            </div>
            <h3 className="font-display text-2xl tracking-tight mt-3">Comment installer l'application</h3>
            <ol className="mt-5 space-y-3 text-sm text-[#1A1918]">
              <li className="flex gap-3"><span className="bg-[#1A1918] text-[#FAFAFA] w-6 h-6 inline-flex items-center justify-center text-xs font-bold shrink-0">1</span><div>Appuyez sur l'icône <strong>Partager</strong> (flèche vers le haut) en bas de Safari.</div></li>
              <li className="flex gap-3"><span className="bg-[#1A1918] text-[#FAFAFA] w-6 h-6 inline-flex items-center justify-center text-xs font-bold shrink-0">2</span><div>Faites défiler et appuyez sur <strong>« Sur l'écran d'accueil »</strong>.</div></li>
              <li className="flex gap-3"><span className="bg-[#1A1918] text-[#FAFAFA] w-6 h-6 inline-flex items-center justify-center text-xs font-bold shrink-0">3</span><div>Appuyez sur <strong>« Ajouter »</strong> en haut à droite.</div></li>
            </ol>
            <div className="mt-6 text-xs text-[#5C5A56]">⚠️ Cette installation ne fonctionne que depuis Safari sur iPhone — pas depuis Chrome.</div>
            <button onClick={dismiss} className="mt-6 w-full bg-[#1A1918] text-[#FAFAFA] py-3 font-semibold hover:bg-[#C86641] transition-colors">Compris</button>
          </div>
        </div>
      )}
    </>
  );
}
